import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

type AdminSidebarProps = {
  collapsed: boolean;
};

type SidebarItem = {
  label: string;
  to?: string;
  icon: string;
  children?: SidebarItem[];
};

const sidebarItems: SidebarItem[] = [
  {
    // Todo el circuito operativo (día a día, recorridos y depósito) vive acá
    // adentro. Las URLs de cada pantalla no cambian, esto sólo las agrupa.
    label: "Operación",
    icon: "◐",
    children: [
      {
        label: "Operación del día",
        to: "/admin/operacion",
        icon: "○",
      },
      {
        // Colectas y repartos no son envíos: son recorridos, y van aparte.
        label: "Recorridos",
        icon: "↝",
        children: [
          {
            label: "Del día",
            to: "/admin/recorridos",
            icon: "≣",
          },
          {
            label: "Colectas fijas",
            to: "/admin/recorridos/fijas",
            icon: "↻",
          },
        ],
      },
      {
        label: "Depósito",
        icon: "▦",
        children: [
          {
            label: "Planta",
            to: "/admin/deposito/planta",
            icon: "▣",
          },
          {
            label: "Estanterías",
            to: "/admin/deposito/estanterias",
            icon: "▦",
          },
        ],
      },
      {
        label: "Choferes en vivo",
        to: "/admin/choferes/mapa",
        icon: "◉",
      },
    ],
  },
  {
    label: "Dashboard",
    to: "/admin",
    icon: "◫",
  },
  {
    label: "Performance",
    to: "/admin/performance",
    icon: "◪",
  },
  {
    label: "Envíos",
    icon: "⬚",
    children: [
      {
        label: "Lista de envíos",
        to: "/admin/envios",
        icon: "≣",
      },
      {
        label: "Lista legacy",
        to: "/admin/envios/legacy",
        icon: "▥",
      },
      {
  label: "Cotizar LynxPack",
  to: "/admin/envios/lynxpack",
  icon: "◈",
},
      {
        label: "Carga de envíos",
        to: "/admin/envios/carga",
        icon: "▣",
      },
      {
        label: "Impresión de etiquetas",
        to: "/admin/envios/etiquetas",
        icon: "▤",
      },
    ],
  },
  {
    // Toda la sección (incluidos los ítems legacy de abajo) sólo se muestra
    // si el usuario tiene finance_access (ver LIQUIDACIONES_ITEM más abajo).
    // Carga manual / Totales son deuda del pipeline legacy viejo, quedan
    // como están hasta que se jubilen — Clientes es la pantalla nueva.
    label: "Liquidaciones",
    icon: "◭",
    children: [
      {
        label: "Dashboard",
        to: "/admin/liquidaciones/dashboard",
        icon: "◫",
      },
      {
        label: "Clientes",
        to: "/admin/liquidaciones/clientes",
        icon: "▤",
      },
      {
        label: "Choferes",
        to: "/admin/liquidaciones/choferes",
        icon: "▥",
      },
      {
        label: "Carga manual",
        to: "/admin/liquidaciones/carga-manual",
        icon: "▣",
      },
      {
        label: "Totales",
        to: "/admin/liquidaciones/totales",
        icon: "◩",
      },
    ],
  },
  {
    // Visible para Coordinador/Liquidaciones/Admin Total (por perfil, ver
    // visibleItems más abajo) — un Coordinador la ve SIN ver "Liquidaciones".
    label: "Reportes Operativos",
    to: "/admin/reportes-operativos",
    icon: "🗒",
  },
  {
    label: "Tiendas",
    to: "/admin/stores",
    icon: "◎",
  },
  {
    label: "Usuarios",
    to: "/admin/users",
    icon: "◌",
  },
  {
    label: "Sistema",
    icon: "▤",
    children: [
      {
        label: "Listas de precios",
        to: "/admin/sistema/listas-de-precios",
        icon: "▥",
      },
    ],
  },
];

const sidebarStyle = (collapsed: boolean): React.CSSProperties => ({
  width: collapsed ? "92px" : "272px",
  minWidth: collapsed ? "92px" : "272px",
  height: "100vh",
  position: "sticky",
  top: 0,
  borderRight: "1px solid var(--border)",
  background: "var(--surface)",
  backdropFilter: "var(--glass)",
  WebkitBackdropFilter: "var(--glass)",
  boxShadow: "var(--shadow)",
  display: "flex",
  flexDirection: "column",
  transition: "width var(--transition-base), min-width var(--transition-base)",
  zIndex: 30,
  overflow: "hidden",
});

const topSectionStyle: React.CSSProperties = {
  padding: "18px 16px 10px 16px",
  flexShrink: 0,
};

const brandRowStyle = (collapsed: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "flex-start",
  gap: "14px",
  minHeight: "52px",
});

const logoBoxStyle: React.CSSProperties = {
  width: "44px",
  height: "44px",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--primary-soft)",
  border: "1px solid var(--border)",
  color: "var(--primary)",
  fontWeight: 800,
  fontSize: "17px",
  letterSpacing: "0.06em",
  flexShrink: 0,
};

const brandMetaStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const brandEyebrowStyle: React.CSSProperties = {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--text-secondary)",
};

const brandNameStyle: React.CSSProperties = {
  fontSize: "17px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const navScrollStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  paddingBottom: "10px",
};

const navStyle: React.CSSProperties = {
  padding: "8px 10px 0 10px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const bottomSectionStyle: React.CSSProperties = {
  padding: "12px 10px 16px 10px",
  flexShrink: 0,
};

const footerCardStyle = (collapsed: boolean): React.CSSProperties => ({
  border: "1px solid var(--border)",
  borderRadius: "16px",
  background: "rgba(255,255,255,0.03)",
  padding: collapsed ? "10px" : "12px",
  textAlign: collapsed ? "center" : "left",
});

const getItemBaseStyle = ({
  active,
  collapsed,
}: {
  active: boolean;
  collapsed: boolean;
}): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "space-between",
  gap: "10px",
  minHeight: "46px",
  padding: collapsed ? "0" : "0 14px",
  borderRadius: "18px",
  textDecoration: "none",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  background: active ? "rgba(198,165,106,0.10)" : "transparent",
  border: active ? "1px solid var(--border)" : "1px solid transparent",
  transition: "all var(--transition-base)",
  fontWeight: active ? 700 : 600,
  fontSize: "14px",
});

const iconWrapStyle: React.CSSProperties = {
  width: "20px",
  textAlign: "center",
  fontSize: "15px",
  flexShrink: 0,
};

const labelStyle: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const leftRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minWidth: 0,
};

const childListStyle = (depth: number): React.CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  paddingTop: "5px",
  paddingLeft: `${10 + (depth - 1) * 8}px`,
});

const getChildStyle = (active: boolean, depth: number): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: "9px",
  minHeight: "36px",
  borderRadius: "14px",
  padding: `0 12px 0 ${12 + (depth - 1) * 10}px`,
  textDecoration: "none",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  background: active ? "rgba(198,165,106,0.08)" : "transparent",
  border: active ? "1px solid var(--border)" : "1px solid transparent",
  transition: "all var(--transition-base)",
  fontSize: "13px",
  fontWeight: active ? 700 : 500,
});

// Un grupo está "activo" si la ruta actual coincide con alguno de sus hijos,
// sin importar cuántos niveles de subgrupo haya en el medio.
const isGroupActive = (item: SidebarItem, currentPath: string): boolean => {
  if (!item.children?.length) return false;
  return item.children.some((child) =>
    child.to ? currentPath.startsWith(child.to) : isGroupActive(child, currentPath)
  );
};

type SidebarNodeProps = {
  item: SidebarItem;
  depth: number;
  collapsed: boolean;
  currentPath: string;
  openGroups: Record<string, boolean>;
  toggleGroup: (key: string) => void;
};

// Un ítem sin hijos es un link; un ítem con hijos es un grupo colapsable que
// se dibuja a sí mismo recursivamente, así "Operación" puede contener tanto
// hojas (Operación del día) como subgrupos (Recorridos, Depósito).
const SidebarNode = ({
  item,
  depth,
  collapsed,
  currentPath,
  openGroups,
  toggleGroup,
}: SidebarNodeProps) => {
  const hasChildren = Boolean(item.children?.length);
  const groupKey = item.to ?? item.label;

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to!}
        end={item.to === "/admin"}
        style={({ isActive }) =>
          depth === 0
            ? getItemBaseStyle({ active: isActive, collapsed })
            : getChildStyle(isActive, depth)
        }
        title={collapsed ? item.label : undefined}
      >
        <div style={leftRowStyle}>
          <span style={iconWrapStyle}>{item.icon}</span>
          {!collapsed && <span style={labelStyle}>{item.label}</span>}
        </div>
      </NavLink>
    );
  }

  const isOpen = openGroups[groupKey];
  const groupActive = isGroupActive(item, currentPath);

  return (
    <div>
      <button
        type="button"
        onClick={() => !collapsed && toggleGroup(groupKey)}
        style={{
          ...(depth === 0
            ? getItemBaseStyle({ active: groupActive, collapsed })
            : getChildStyle(groupActive, depth)),
          width: "100%",
          cursor: "pointer",
        }}
        title={collapsed ? item.label : undefined}
      >
        <div style={leftRowStyle}>
          <span style={iconWrapStyle}>{item.icon}</span>
          {!collapsed && <span style={labelStyle}>{item.label}</span>}
        </div>

        {!collapsed && (
          <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
            {isOpen ? "▾" : "▸"}
          </span>
        )}
      </button>

      {!collapsed && isOpen && (
        <div style={childListStyle(depth + 1)}>
          {item.children!.map((child) => (
            <SidebarNode
              key={child.to ?? child.label}
              item={child}
              depth={depth + 1}
              collapsed={collapsed}
              currentPath={currentPath}
              openGroups={openGroups}
              toggleGroup={toggleGroup}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AdminSidebar = ({ collapsed }: AdminSidebarProps) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { financeAccess, adminProfile } = useAuth();
  const canSeeReports = adminProfile === "COORDINADOR" || adminProfile === "LIQUIDACIONES" || adminProfile === "ADMIN_TOTAL";

  // "Liquidaciones" es financiero: se oculta del todo sin finance_access.
  // "Reportes Operativos" se muestra por perfil, no por finance_access —
  // así un Coordinador lo ve sin ver Liquidaciones. La autoridad real está
  // en el backend (requireAdminPermit/requireProfile en cada endpoint) —
  // esto es sólo para no mostrar una puerta que el server va a cerrar con 403.
  const visibleItems = useMemo(
    () =>
      sidebarItems.filter((item) => {
        if (item.label === "Liquidaciones") return financeAccess;
        if (item.label === "Reportes Operativos") return canSeeReports;
        return true;
      }),
    [financeAccess, canSeeReports]
  );
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <aside style={sidebarStyle(collapsed)}>
      <div style={topSectionStyle}>
        <div style={brandRowStyle(collapsed)}>
          <div style={logoBoxStyle}>LX</div>

          {!collapsed && (
            <div style={brandMetaStyle}>
              <span style={brandEyebrowStyle}>Plataforma logística</span>
              <span style={brandNameStyle}>Lynx</span>
            </div>
          )}
        </div>
      </div>

      <div style={navScrollStyle}>
        <nav style={navStyle}>
          {visibleItems.map((item) => (
            <SidebarNode
              key={item.to ?? item.label}
              item={item}
              depth={0}
              collapsed={collapsed}
              currentPath={currentPath}
              openGroups={openGroups}
              toggleGroup={toggleGroup}
            />
          ))}
        </nav>
      </div>

      <div style={bottomSectionStyle}>
        <div style={footerCardStyle(collapsed)}>
          {!collapsed ? (
            <>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-secondary)",
                  marginBottom: "6px",
                }}
              >
                Entorno
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                Panel administrativo
              </div>
            </>
          ) : (
            <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
              ●
            </span>
          )}
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;